import React, { useState, cloneElement } from 'react';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { FileText, ShoppingBag, CreditCard, Heart, MessageSquare, Settings, LogOut, Download, CalendarDays, Sparkles, Star, AlertCircle, Package, Video, Shield, HelpCircle, Menu, X } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useUser } from '../contexts/UserContext';
import { zodiacThemes, getZodiacSign } from '../components/user-dashboard/zodiacThemes';
import { OverviewTab } from '../components/user-dashboard/OverviewTab';
import { ProfileTab } from '../components/user-dashboard/ProfileTab';
import { CalendarTab } from '../components/user-dashboard/CalendarTab';
import { ReportsTab } from '../components/user-dashboard/ReportsTab';
import { OrdersTab } from '../components/user-dashboard/OrdersTab';
import { CreditsTab } from '../components/user-dashboard/CreditsTab';
import { SubscriptionsTab } from '../components/user-dashboard/SubscriptionsTab';
import { FavoritesTab } from '../components/user-dashboard/FavoritesTab';
import { ChatTab } from '../components/user-dashboard/ChatTab';
import { SettingsTab } from '../components/user-dashboard/SettingsTab';
import { PurchasesTab } from '../components/user-dashboard/PurchasesTab';
import { ConsultationsUserTab } from '../components/user-dashboard/ConsultationsUserTab';
import { SecurityTab } from '../components/user-dashboard/SecurityTab';
import { SupportTab } from '../components/user-dashboard/SupportTab';
interface UserDashboardPageProps {
  onNavigate: (page: string) => void;
}
export function UserDashboardPage({
  onNavigate
}: UserDashboardPageProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const {
    user,
    logout
  } = useUser();
  // User Astro Data State
  const [birthData, setBirthData] = useState({
    date: '1990-04-12',
    time: '14:30',
    place: 'Москва, Россия'
  });
  const currentSign = getZodiacSign(birthData.date);
  const theme = zodiacThemes[currentSign];
  // Grouped Menu Structure
  const menuGroups = [{
    title: 'ОСНОВНОЕ',
    items: [{
      id: 'overview',
      label: 'Обзор',
      icon: <FileText className="w-5 h-5" data-id="element-25831" />
    }, {
      id: 'orders',
      label: 'Мои заказы',
      icon: <ShoppingBag className="w-5 h-5" data-id="element-25832" />
    }, {
      id: 'chat',
      label: 'Сообщения',
      icon: <MessageSquare className="w-5 h-5" data-id="element-25833" />
    }]
  }, {
    title: 'МОЯ АСТРОЛОГИЯ',
    items: [{
      id: 'reports',
      label: 'Мои отчёты',
      icon: <Download className="w-5 h-5" data-id="element-25834" />
    }, {
      id: 'profile',
      label: 'Мой астропрофиль',
      icon: <Sparkles className="w-5 h-5" data-id="element-25835" />
    }, {
      id: 'consultations',
      label: 'Мои консультации',
      icon: <Video className="w-5 h-5" data-id="element-25836" />
    }, {
      id: 'calendar',
      label: 'Мой календарь',
      icon: <CalendarDays className="w-5 h-5" data-id="element-25837" />
    }]
  }, {
    title: 'ПОКУПКИ',
    items: [{
      id: 'purchases',
      label: 'Мои покупки',
      icon: <Package className="w-5 h-5" data-id="element-25838" />
    }, {
      id: 'favorites',
      label: 'Избранное',
      icon: <Heart className="w-5 h-5" data-id="element-25839" />
    }, {
      id: 'credits',
      label: 'Баланс и оплаты',
      icon: <CreditCard className="w-5 h-5" data-id="element-25840" />
    }, {
      id: 'subscriptions',
      label: 'Подписка',
      icon: <Star className="w-5 h-5" data-id="element-25841" />
    }]
  }, {
    title: 'АККАУНТ',
    items: [{
      id: 'settings',
      label: 'Настройки',
      icon: <Settings className="w-5 h-5" data-id="element-25842" />
    }, {
      id: 'security',
      label: 'Безопасность',
      icon: <Shield className="w-5 h-5" data-id="element-25843" />
    }, {
      id: 'support',
      label: 'Правила и поддержка',
      icon: <HelpCircle className="w-5 h-5" data-id="element-25844" />
    }]
  }];
  // Flatten items for mobile quick scroll if needed, or just use the hamburger menu
  const handleTabChange = (id: string) => {
    setActiveTab(id);
    setMobileMenuOpen(false);
  };
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab theme={theme} user={user} setActiveTab={setActiveTab} data-id="element-25845" />;
      case 'orders':
        return <OrdersTab theme={theme} onNavigate={onNavigate} setActiveTab={setActiveTab} data-id="element-25846" />;
      case 'chat':
        return <ChatTab theme={theme} data-id="element-25847" />;
      case 'reports':
        return <ReportsTab theme={theme} onNavigate={onNavigate} data-id="element-25848" />;
      case 'profile':
        return <ProfileTab theme={theme} birthData={birthData} setBirthData={setBirthData} setActiveTab={setActiveTab} data-id="element-25849" />;
      case 'consultations':
        return <ConsultationsUserTab theme={theme} onNavigate={onNavigate} setActiveTab={setActiveTab} data-id="element-25850" />;
      case 'calendar':
        return <CalendarTab theme={theme} data-id="element-25851" />;
      case 'purchases':
        return <PurchasesTab onNavigate={onNavigate} data-id="element-25852" />;
      case 'favorites':
        return <FavoritesTab theme={theme} onNavigate={onNavigate} data-id="element-25853" />;
      case 'credits':
        return <CreditsTab theme={theme} onNavigate={onNavigate} data-id="element-25854" />;
      case 'subscriptions':
        return <SubscriptionsTab theme={theme} onNavigate={onNavigate} data-id="element-25855" />;
      case 'settings':
        return <SettingsTab theme={theme} user={user} avatarUrl={avatarUrl} onAvatarChange={setAvatarUrl} data-id="element-25856" />;
      case 'security':
        return <SecurityTab theme={theme} user={user} data-id="element-25857" />;
      case 'support':
        return <SupportTab theme={theme} setActiveTab={setActiveTab} data-id="element-25858" />;
      default:
        return <div className="flex flex-col items-center justify-center py-20 text-slate-500" data-id="element-25859">
            <AlertCircle className="w-12 h-12 mb-4 text-slate-300" data-id="element-25860" />
            <p data-id="element-25861">Раздел в разработке</p>
          </div>;
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24 font-sans" data-id="element-25862">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 lg:pt-12" data-id="element-25863">
        {/* Mobile Header & Menu Toggle */}
        <div className="lg:hidden flex items-center justify-between bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-6" data-id="element-25864">
          <div className="flex items-center gap-3" data-id="element-25865">
            <div className={`w-10 h-10 rounded-xl ${avatarUrl ? '' : `bg-gradient-to-br ${theme.gradient}`} flex items-center justify-center text-white font-bold overflow-hidden`} data-id="element-25866">
              {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="w-full h-full object-cover" data-id="element-25867" /> : theme.icon}
            </div>
            <div data-id="element-25868">
              <div className="font-bold text-slate-900 text-sm" data-id="element-25869">
                {user?.name || 'Пользователь'}
              </div>
              <div className={`text-xs font-medium ${theme.text}`} data-id="element-25870">
                {theme.name}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-25871">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-25872" /> : <Menu className="w-6 h-6" data-id="element-25873" />}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-25874">
          {/* Sidebar (Desktop & Mobile Dropdown) */}
          <div className={`lg:col-span-3 ${mobileMenuOpen ? 'block' : 'hidden lg:block'}`} data-id="element-25875">
            <Card padding="none" className="sticky top-24 overflow-hidden shadow-sm mb-6 lg:mb-0" data-id="element-25876">
              {/* Desktop User Info */}
              <div className={`hidden lg:block p-6 border-b ${theme.border} bg-gradient-to-b ${theme.lightGradient}`} data-id="element-25877">
                <div className="flex flex-col items-center text-center" data-id="element-25878">
                  <div className={`w-20 h-20 rounded-full ${avatarUrl ? '' : `bg-gradient-to-br ${theme.gradient}`} flex items-center justify-center text-3xl text-white shadow-md mb-3 relative overflow-hidden`} data-id="element-25879">
                    {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="w-full h-full object-cover" data-id="element-25880" /> : theme.icon}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center text-xs font-bold text-slate-700 shadow-sm uppercase" data-id="element-25881">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  </div>
                  <div className="font-bold text-slate-900 text-lg" data-id="element-25882">
                    {user?.name || 'Пользователь'}
                  </div>
                  <div className={`text-xs font-bold uppercase tracking-wider mt-1 ${theme.text}`} data-id="element-25883">
                    {theme.name}
                  </div>
                </div>
              </div>

              {/* Grouped Navigation Menu */}
              <nav className="p-3 max-h-[70vh] lg:h-[calc(100vh-300px)] overflow-y-auto custom-scrollbar" data-id="element-25884">
                {menuGroups.map((group, idx) => <div key={idx} className="mb-4 last:mb-0" data-id="element-25885">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-3" data-id="element-25886">
                      {group.title}
                    </div>
                    {group.items.map(item => <button key={item.id} onClick={() => handleTabChange(item.id)} title={item.label} className={`w-full flex items-center px-3 py-2 mb-0.5 rounded-lg text-sm font-medium transition-colors ${activeTab === item.id ? `${theme.bg} ${theme.text} ring-1 ${theme.ring}` : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-25887">
                        <span className={`mr-3 shrink-0 ${activeTab === item.id ? theme.text : 'text-slate-400'}`} data-id="element-25888">
                          {item.icon}
                        </span>
                        <span className="truncate" data-id="element-25889">{item.label}</span>
                      </button>)}
                  </div>)}

                <div className="pt-4 mt-2 border-t border-slate-100" data-id="element-25890">
                  <button onClick={() => {
                  logout();
                  onNavigate('home');
                }} className="w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-25891">
                    <LogOut className="w-5 h-5 mr-3 shrink-0 text-rose-400" data-id="element-25892" />
                    <span className="truncate" data-id="element-25893">Выйти</span>
                  </button>
                </div>
              </nav>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className={`lg:col-span-9 ${mobileMenuOpen ? 'hidden lg:block' : 'block'}`} data-id="element-25894">
            <SectionErrorBoundary key={activeTab} sectionName={`user-dashboard-${activeTab}`} fallbackMinHeight="min-h-[400px]" data-id="element-25895">
              {renderContent()}
            </SectionErrorBoundary>
          </div>
        </div>
      </div>
    </div>;
}